import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { FeatureFlag, Permission } from '@core/enums';
import { FieldDef, QueryConfig, UserAssociation } from '@core/models';
import { AccountService } from '@core/services';
import { LookupService } from '@services/lookup.service';
import { ColumnSelectorModule } from '@shared/components/column-selector/column-selector.module';
import { SelectableFieldDef } from '@shared/components/column-selector/column-selector.types';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { RelatedToType } from '@shared/enums';
import { defaultTaskTableFields } from '@shared/modules/legacy-tasks/list/list.constants';
import { AccountState } from '@state/account/state';
import { FieldDefsState } from '@state/fields/state';
import { UserState } from '@state/user/state';
import { LoadingComponent } from '@tasks/loading/loading.component';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { TaskBulkUpdateComponent } from '@tasks/task-shell/task-bulk-update/task-bulk-update.component';
import { TaskViewBase } from '@tasks/task-view-base';
import { TaskList, TaskView, TaskViewType } from '@tasks/tasks.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { InviteUserComponent } from 'app/modules/settings/modals/invite-user/invite-user.component';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, debounceTime, filter, finalize, map, Observable, take, tap } from 'rxjs';
import {
  FilterListItemChangeArgs,
  FilterListItemModel,
  FilterListItemValueModel,
  TsBadgeModule,
  TsButtonModule,
  TsFilterModule,
  TSIconModule,
  TsInputComponent,
  TsOverlayComponent,
  TsOverlayModule,
} from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { GroupByOption, TsGroupByComponent } from 'tsui/lib/ts-group-by/ts-group-by/ts-group-by.component';

@Component({
  selector: 'app-task-header-action-bar',
  standalone: true,
  imports: [
    CommonModule,
    DirectivesModule,
    TsButtonModule,
    TsBadgeModule,
    LoadingComponent,
    MatLegacyTooltipModule,
    MatLegacyCheckboxModule,
    MatMenuModule,
    TsFilterModule,
    TSIconModule,
    TsInputComponent,
    TsOverlayModule,
    ColumnSelectorModule,
    MatLegacyOptionModule,
    MatLegacySelectModule,
    ReactiveFormsModule,
    FormsModule,
    TsGroupByComponent,
  ],
  template: `
    <section class="task-view__controllers flex flex-row gap-2">
      <article class="views-wrapper">
        <ng-container *ngFor="let view of taskViewsConfig">
          <span
            class="lt-md:hidden view-option"
            [matTooltip]="view.tooltip"
            [attr.data-testid]="'task-header-view-type-' + view.name"
            (click)="changeTasksView(view.name)"
            [ngClass]="{ 'active-state': viewType === view.name }"
          >
            <ts-icon [icon]="view.icon"></ts-icon>
          </span>
        </ng-container>
      </article>
      <!-- User Association -->
      <section class="ml-4 mr-2 flex items-center w-full">
        <ng-container *ngFor="let userAssociation of userAssociations; let i = index">
          <img
            src="{{ userAssociation?.user?.avatar_url }}"
            class="w-6 h-6 m-2px rounded-full text-card shadow-solid"
            [ngClass]="{ '-ml-3': i > 0 }"
            alt="{{ userAssociation.user.full_name }}"
          />
        </ng-container>
        <ts-button
          [appAuthorize]="allowSharingAndExport"
          type="icon"
          color="accent"
          data-dd-action-name="Tasks - Action Bar - Add User"
          [attr.data-testid]="'task-header-add-user'"
          icon="add_circle"
          [matTooltip]="'Add User'"
          [ngClass]="{ 'ml-2': userAssociations?.length > 0 }"
          [tsOverlayTriggerFor]="userAssociationsOverlayBox"
        ></ts-button>
      </section>

      <!-- Search Bar -->
      <ts-input
        [formControl]="searchInputControl"
        type="text"
        icon="search"
        placeholder="Search"
        class="ml-2 search-bar"
        [attr.data-testid]="'task-header-search-input'"
      ></ts-input>

      <!-- Display Select Panel -->
      <app-column-selector
        *ngIf="viewType === 'list' && canEditTaskLayout && taskFields && selectedColumns"
        [matTooltip]="'Select columns'"
        [items]="taskFields"
        [selectedOrder]="selectedColumns"
        [draggable]="true"
        [showCountBadge]="true"
        [isButton]="true"
        (onSave)="onSaveColumns($event)"
      ></app-column-selector>

      <!-- List Filter Button -->
      <ts-button
        type="stroked"
        color="accent"
        [tsOverlayTriggerFor]="filtersOverlayBox"
        [matTooltip]="'Apply filters'"
        [attr.data-testid]="'filter-top-button'"
        data-dd-action-name="Tasks - Action Bar - Open Filters"
      >
        <ts-icon icon="ts_filter-list" class="mr-2"></ts-icon>
        Filter
        <ng-container *ngIf="filterCounter">
          <ts-badge [value]="filterCounter" [type]="'info'"></ts-badge>
        </ng-container>
      </ts-button>

      <div class="relative">
        @if (viewType === 'list' && !newSectionEnabled) {
          <ts-button
            [debounceTime]="0"
            type="stroked"
            color="accent"
            data-dd-action-name="Tasks - Action Bar - List View"
            [attr.data-testid]="'task-header-open-groupby'"
            [matTooltip]="groupBy ? 'Grouped by: ' + getGroupByLabel() : 'Group tasks'"
            class=""
            (click)="openGroupPanel()"
          >
            <ts-icon icon="ts_folder" class="mr-2"></ts-icon>
            Group
            <ng-container *ngIf="groupBy.length">
              <ts-badge [value]="getGroupByLabel()" [type]="'success'"></ts-badge>
            </ng-container>
          </ts-button>
        } @else if (newSectionEnabled) {
          <ts-button
            type="stroked"
            color="accent"
            [tsOverlayTriggerFor]="groupByOverlayBox"
            [matTooltip]="'Group By'"
            [attr.data-testid]="'filter-top-button'"
            data-dd-action-name="Tasks - Action Bar - Open Group By"
          >
            <ts-icon icon="ts_folder" class="mr-2"></ts-icon>
            Group By
            <ng-container *ngIf="groupBy.length">
              <ts-badge [value]="groupBy.length" [type]="'info'"></ts-badge>
            </ng-container>
          </ts-button>
        }

        <mat-form-field class="absolute top-0 left-0 invisible">
          <mat-select #groupSelect [(ngModel)]="groupBy" (selectionChange)="groupByChanged.emit($event.value)">
            <mat-option value="">None</mat-option>
            <mat-option *ngFor="let fieldDef of groupFields" [value]="fieldDef.name">
              {{ fieldDef.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      @if (selectedTasks$ | async; as selectedTasks) {
        @if (selectedTasks > 0) {
          <ts-button
            [matMenuTriggerFor]="menu"
            class="h-full w-full flex flex-row justify-start items-center"
            variant="secondary"
            icon="ts_bulk"
            [attr.data-testid]="'task-header-open-bulk-action'"
            data-dd-action-name="Tasks - Action Bar - Open Bulk Action"
          >
            <span> Bulk Action </span>
            <ts-badge [value]="selectedTasks" [type]="'info'"></ts-badge>
          </ts-button>
          <!-- <ts-button
            type="stroked"
            color="accent"
            class="ml-4"
            [matTooltip]="'Apply filters'"
            (click)="openBulkUpdateTasks()"
          >
            <ts-icon icon="ts_bulk" class="mr-2"></ts-icon>
            Bulk Action
          </ts-button> -->
        }
      }

      <mat-menu #menu="matMenu">
        <button
          mat-menu-item
          (click)="openBulkUpdateTasks()"
          data-dd-action-name="Tasks - Action Bar - Bulk Edit"
          [attr.data-testid]="'task-header-open-bulk-edit'"
        >
          <div class="btn-content">
            <ts-icon outlined="true" [color]="'black'" icon="ts_pen"></ts-icon>
            <span>Edit</span>
          </div>
        </button>
        <button
          mat-menu-item
          (click)="openDuplicateModal()"
          data-dd-action-name="Tasks - Action Bar - Bulk Duplicate"
          [attr.data-testid]="'task-header-open-bulk-duplicate'"
        >
          <div class="btn-content">
            <ts-icon outlined="true" [color]="'black'" icon="ts_copy"></ts-icon>
            <span>Duplicate</span>
          </div>
        </button>
        <button
          mat-menu-item
          (click)="openDeleteModal()"
          data-dd-action-name="Tasks - Action Bar - Bulk Delete"
          [attr.data-testid]="'task-header-open-bulk-delete'"
        >
          <div class="btn-content" class="text-danger">
            <ts-icon outlined="true" [color]="'red'" icon="ts_trash"></ts-icon>
            <span>Delete</span>
          </div>
        </button>
      </mat-menu>
    </section>

    <!-- User Associations Panel -->
    <ts-overlay
      #userAssociationsOverlayBox
      position="bottom"
      [transparentBackdrop]="true"
      (closeByBackdropClick)="onUserAssociationPanelClose(newTagInput)"
    >
      <div class="users-details-tags-panel">
        <div class="users-panel-header">
          <div class="new-user">
            <mat-icon [svgIcon]="'search'"></mat-icon>
            <!-- <input type="text" placeholder="Find user" #newTagInput /> -->
            <input type="text" placeholder="Find user" (input)="filterUserAssociations($event)" #newTagInput />
          </div>
        </div>

        <div class="available-users">
          <ng-container>
            <mat-checkbox
              *ngFor="let userAssociation of filteredUserAssociations"
              [color]="'primary'"
              [checked]="isAssociatedUser(userAssociation.user.id)"
              (change)="toggleUserAssociation(userAssociation, $event)"
              [disabled]="!userAssociation.user?.active"
            >
              {{ userAssociation.user.full_name }}
            </mat-checkbox>
          </ng-container>
        </div>
        <div [appAuthorize]="allowSharingAndExport" class="create-user" (click)="openInvitationModal()" matRipple>
          <mat-icon [svgIcon]="'add'"></mat-icon>
          <div>Invite Guest User</div>
        </div>
      </div>
    </ts-overlay>

    <!-- List Filters Panel -->
    <ts-overlay
      #filtersOverlayBox
      position="bottom"
      [transparentBackdrop]="true"
      (closeByBackdropClick)="onCloseByBackdropClick()"
    >
      <ts-filter
        size="large"
        [fields]="typeFields | async"
        [filters]="filterValueList | async"
        [lookupContract]="lookupService"
        [hasGroups]="false"
        (addItem)="onAddFilterItem()"
        (filterDefChange)="onFilterDefChanged($event)"
        (filterRemove)="onFilterRemoved($event)"
        (filterClear)="onFilterCleared()"
        (filterApply)="onFilterApplied()"
      ></ts-filter>
    </ts-overlay>

    <!-- GroupBy Panel -->
    <ts-overlay
      #groupByOverlayBox
      position="bottom"
      [transparentBackdrop]="true"
      (closeByBackdropClick)="onCloseByBackdropClick()"
    >
      @if (newSectionEnabled) {
        <ts-group-by
          [options]="groupByOptions"
          [selectedOptions]="groupBy"
          (groupsChange)="onNewGroupByChange($event)"
        ></ts-group-by>
      }
    </ts-overlay>
  `,
  styleUrl: './task-header-action-bar.component.scss',
})
export class TaskHeaderActionBarComponent extends TaskViewBase implements OnInit {
  @Input() allowSharingAndExport: boolean = false;

  @Output() taskViewChange = new EventEmitter<string>();
  @Output() applyFilter = new EventEmitter<Partial<QueryConfig.QueryFilters>>();
  @Output() clearFilter = new EventEmitter<void>();
  @Output() columnsChanged = new EventEmitter<string[]>();
  @Output() groupByChanged = new EventEmitter<string | string[]>();
  groupBy: string | string[] = '';

  public taskViewsConfig = [
    { name: 'list', tooltip: 'List view', icon: 'reorder' },
    { name: 'gantt', tooltip: 'Gantt view', icon: 'clear_all' },
    { name: 'kanban', tooltip: 'Kanban view', icon: 'view_agenda' },
    { name: 'calendar', tooltip: 'Calendar view', icon: 'calendar_today' },
  ];

  @ViewChild('userAssociationsOverlayBox') private _userAssociationOverlayBox: TsOverlayComponent;
  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;
  @ViewChild('groupByOverlayBox') private _groupByOverlayBox: TsOverlayComponent;
  @ViewChild('groupSelect') groupSelect: MatSelect;

  private projectsService = inject(ProjectsService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private accountService = inject(AccountService);
  public lookupService = inject(LookupService);

  protected destroyRef = inject(DestroyRef);
  private dialog = inject(Dialog);
  private _taskList: TaskList;
  private confirmationService = inject(ConfirmationDialogService);

  viewType$: Observable<TaskViewType> = this.store.select(TaskState.getViewType);
  selectedTasks$: Observable<number> = this.store
    .select(TaskState.getSelectedTasks)
    .pipe(map((tasks) => tasks.length))
    .pipe(tap((count) => (this.selectedTasks = count)));
  taskView$: Observable<TaskView> = this.store.select(TaskState.getTaskView).pipe(filter((taskList) => !!taskList));

  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  selectedTasks: number;
  relatedToType = RelatedToType;
  viewType: TaskViewType;
  searchInputControl: FormControl;
  queryFilters;
  userAssociations;
  accountUserAssociations;
  filteredUserAssociations;
  users;
  taskFields;
  canEditTaskLayout = false;
  selectedColumns;
  grouppableFields = ['workflow_state', 'assigned_user_id', 'task_list_id'];
  aggTaskGroupFields = ['project_title', 'property_name'];
  groupFields: FieldDef[] = [];
  groupByOptions: GroupByOption[] = [];
  newSectionEnabled = false;

  public getGroupByLabel(): string {
    return this.taskFields.find((taskFieldDef: FieldDef) => taskFieldDef.name === this.groupBy)?.label;
  }

  public openGroupPanel(): void {
    this.groupSelect.open();
  }

  get taskList(): TaskList {
    return this._taskList;
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.newSectionEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TASK_SECTION));
    this.canEditTaskLayout = this.store.selectSnapshot(UserState.hasPermission(Permission.TASK_LIST_EDIT_LAYOUT));
    this.searchInputControl = new FormControl('');
    this.searchInputControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
      .subscribe((query) => {
        this.queryFilters.q = query;
        this.onFilterApplied();
      });

    this.viewType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((viewType) => {
      this.viewType = viewType;
    });

    this.taskView$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const taskListIdHasChanged = this._taskList?.id !== value.id;
      this.queryFilters = value.definition?.filters ? cloneDeep(JSON.parse(value.definition.filters)) : {};
      this.selectedColumns = value.definition?.fields || defaultTaskTableFields.map(({ name }) => name);
      this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
      this.searchInputControl.patchValue(this.queryFilters?.q || '', { emitEvent: false }); // TODO MJR FILL THE SEARCH INPUT
      this.userAssociations = [];
      this.groupBy = value.definition?.table?.groupBy || '';
      if (this.newSectionEnabled) {
        this.groupBy = Array.isArray(value.definition?.table?.groupBy)
          ? value.definition?.table?.groupBy
          : [value.definition?.table?.groupBy];
      }

      this._taskList = value;
      if (taskListIdHasChanged) {
        this.loadTaskListUserAssociations();
      }
      this.setFilteredFields();
      this.changeDetectorRef.detectChanges();
    });

    if (this.allowSharingAndExport) {
      this.accountService.userAssociations$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((userAssociations: UserAssociation[]) => {
          this.accountUserAssociations = userAssociations.filter((ua) => ua.user);
          this.filteredUserAssociations = userAssociations.filter((ua) => ua.user);
          this.users = userAssociations.map((userAssociation) => userAssociation.user);
        });
    }
  }

  changeTasksView(viewName: string) {
    this.taskViewChange.emit(viewName);
  }

  onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  onNewGroupByChange(value): void {
    this.groupByChanged.emit(value);
    this._groupByOverlayBox.closeOverlay();
  }

  filterUserAssociations(event: InputEvent): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();

    this.filteredUserAssociations = this.accountUserAssociations.filter((ua) =>
      ua.user.full_name?.toLowerCase().includes(value),
    );
  }

  onUserAssociationPanelClose(element: HTMLInputElement): void {
    element.value = '';
  }

  public onSaveColumns(selectedFieldDefs: SelectableFieldDef[]): void {
    const selectedFields = selectedFieldDefs.length
      ? selectedFieldDefs
      : (defaultTaskTableFields as SelectableFieldDef[]);

    if (!selectedFields.find(({ name }) => name === 'title')) {
      selectedFields.unshift({
        name: 'title',
        label: 'Title',
        selected: true,
        type: 'text',
      });
    }

    this.selectedColumns = selectedFieldDefs.map(({ name }) => name) || [];

    this.columnsChanged.emit(this.selectedColumns);
  }

  getTaskListSharingRecordsPermission() {
    return true;
  }

  isAssociatedUser(userId: number): boolean {
    return this.userAssociations?.find((ua) => ua.user?.id === userId) !== undefined;
  }

  toggleUserAssociation(userAssociation: UserAssociation, change: MatCheckboxChange): void {
    if (change.checked) {
      this.addUserAssociation(userAssociation);
    } else {
      this.removeUserAssociation(userAssociation);
    }
  }

  openInvitationModal(): void {
    this._userAssociationOverlayBox.closeOverlay();

    this.dialog.open(InviteUserComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        role: 'guest',
        forceRoleName: 'collaborator',
        extra: { task_list_id: this.taskList?.id },
      },
    });
  }

  openBulkUpdateTasks(): void {
    this.dialog
      .open(TaskBulkUpdateComponent, { ...TsDialogComponent.getDefaultDialogConfig() })
      .closed.subscribe((res) => {
        if (Object.keys(res ?? {}).length) {
          this.store.dispatch(new TaskActions.BulkUpdateSelectedTasks(res));
        }
      });
  }

  openDuplicateModal(): void {
    this.confirmationService
      .openConfirmationDialog({
        dialogTitle: `Duplicate ${this.selectedTasks} selected tasks`,
        description: 'This will create a exact copy of the selected tasks.',
      })
      .pipe(filter((result) => result))
      .subscribe(() => {
        this.store.dispatch(new TaskActions.BulkDuplicateSelectedTasks());
      });
  }

  openDeleteModal(): void {
    // count all substasks as well
    const totalSubtasks = this.store.selectSnapshot(TaskState.getSelectedTasks).reduce((acc, task) => {
      return acc + (task.children ? task.children.length : 0);
    }, 0);

    this.confirmationService
      .openConfirmationDialog({
        dialogTitle: `Delete ${this.selectedTasks} selected tasks`,
        description: `This action is not reversible. ${totalSubtasks > 0 ? `It will also delete ${totalSubtasks} subtasks.` : ''}`,
      })
      .pipe(filter((result) => result))
      .subscribe(() => {
        this.store.dispatch(new TaskActions.BulkDeleteSelectedTasks());
      });
  }

  private removeUserAssociation(userAssociation: UserAssociation): void {
    // move it to task action
    const ua = this.userAssociations.find((item) => item.user.id === userAssociation.user.id);
    this.projectsService
      .deleteUserAssociationGeneral(ua)
      .pipe(finalize(() => this.loadTaskListUserAssociations()))
      .subscribe();
  }

  private addUserAssociation(userAssociation: UserAssociation): void {
    // move it to task action
    const ua = {
      related_to_id: this.taskList?.id || this.taskList.id,
      related_to_type: RelatedToType.TaskList,
      user_id: userAssociation.user.id,
      role: userAssociation.role,
    };
    this.projectsService
      .createUserAssociationGeneral(ua)
      .pipe(finalize(() => this.loadTaskListUserAssociations()))
      .subscribe();
  }

  private loadTaskListUserAssociations(): void {
    const account = this.store.selectSnapshot(AccountState.getAccount);
    this.projectsService.accountId = account?.id;
    this.projectsService
      .getUserAssociationsGeneral('TaskList', this.taskList?.id)
      .pipe(take(1))
      .subscribe((userAssociations) => {
        if (userAssociations) {
          this.userAssociations = userAssociations.filter((ua) => ua.user);
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private setFilteredFields(): void {
    this.taskFields = cloneDeep(
      this.taskFields
        .map((fieldDef) => ({
          ...fieldDef,
          selected: this.selectedColumns?.includes(fieldDef.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    );

    if (this.isAggregatedTask) {
      this.grouppableFields = [...this.grouppableFields, ...this.aggTaskGroupFields];
    }

    this.groupFields = this.taskFields.filter((field) => this.grouppableFields.includes(field.name));

    if (this.newSectionEnabled) {
      this.groupByOptions = this.groupFields.map((field) => {
        return {
          key: field.name,
          label: field.label,
        };
      });
    }

    this.setFilterNestedFields();
  }

  private setFilterNestedFields(): void {
    const allfields = this.taskFields.map((x) => ({
      ...x,
      meta: { ...x.meta, group: { key: 'Task', label: 'Task' } },
    }));

    const applicableType = this.store.selectSnapshot(TaskState.getTaskView).related_to_type;

    this.typeFields.next(
      allfields.map(({ label, name, type, meta }) => {
        const fieldOptions = this.formatFieldOptions(meta);

        let innerFilter = null;

        if (name === 'assigned_task_role_id') {
          if (this.isTaskListTemplate) {
            innerFilter = (options): any[] =>
              options.filter(
                (x) => x.applicableType === this.store.selectSnapshot(TaskState.getTaskView).applicable_type,
              );
          } else if (!this.isAggregatedTask) {
            innerFilter = (options): any[] => options.filter((x) => x.applicableType === applicableType);
          }
        }

        return {
          label,
          name,
          meta: {
            filtering: { operators: meta?.filtering?.operators || [], functions: meta?.filtering?.functions || [] },
            options: fieldOptions,
            multi_select: meta?.multi_select || false,
            record_type: meta?.record_type || null,
            group: meta?.group,
            scale: meta?.scale,
            innerFilter,
          },
          type,
        } as FilterListItemModel;
      }),
    );
  }

  private formatFieldOptions(meta: any): string[] | { label: string; value: string; applicableType?: string }[] {
    const dropdownOptions = this.store
      .selectSnapshot(FieldDefsState.selectDropdownOptionsBy(meta?.option_name))
      ?.filter((x) => !!x);

    if (dropdownOptions) {
      return dropdownOptions.map((option) => ({
        label: option.name,
        value: option.id,
        applicableType: option.applicable_type,
      }));
    }

    return meta?.options || [];
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    // this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { ...this.queryFilters, fjson: `[]` };
    this.clearFilter.emit();
  }

  public onFilterApplied(): void {
    // this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      ...this.queryFilters,
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
    };

    if (this.hasFilter()) {
      this.applyFilter.emit(this.queryFilters);
    } else {
      this.clearFilter.emit();
    }
    this._filtersOverlayBox.closeOverlay();
  }

  private hasFilter(): boolean {
    return this.filterValueList.value.length > 0 || this.searchInputControl.value.trim() !== '';
  }
}
